class ProductMatchingRows extends HTMLElement {
  constructor() {
    super();
    this.selectedColor = this.getAttribute('data-color') || '';
    this.rows = this.querySelectorAll('.matching-row');
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.changeColorOptions = this.changeColorOptions.bind(this);
    this.init();
  }

  static get observedAttributes() {
    return ['data-color'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-color' && oldValue !== newValue) {
      this.updateRowsByColor(newValue);
    }
  }

  init() {
    this.rows.forEach(row => {
      this.setupRow(row);
      const select = row.querySelector('.matching-row__select');
      
      let initialOption = select.options[select.selectedIndex];
      
      // If global color is set, try to find matching option
      if (this.selectedColor) {
        const matched = this.findOptionByColor(select, this.selectedColor);
        if (matched) {
          initialOption = matched;
          select.value = matched.value;
        }
      }

      this.updateRowUI(row, initialOption);
    });
    this.initShowMore();
    
    // Add single global click listener instead of one per row
    document.addEventListener('click', this.handleOutsideClick);
    if (this.getAttribute('data-from') != 'cart') {
      document.addEventListener('color-changed', this.changeColorOptions);
    }
  }

  // Helper: Find option by color (Exact match > IndexOf)
  findOptionByColor(select, color) {
    if (!color || !select) return null;
    
    const targetColor = color.trim().toLowerCase();
    const options = Array.from(select.options);
    
    // 1. Exact match (Case-insensitive)
    let matchedOption = options.find(opt => opt.textContent.trim().toLowerCase() === targetColor);
    
    // 2. Fallback to indexOf (Case-insensitive)
    if (!matchedOption) {
      matchedOption = options.find(opt => opt.textContent.toLowerCase().indexOf(targetColor) > -1);
    }
    
    return matchedOption;
  }

  // Handle clicks outside custom selects
  handleOutsideClick(e) {
    this.querySelectorAll('.custom-select').forEach(customSelect => {
      if (!customSelect.contains(e.target)) {
        customSelect.classList.remove('open');
      }
    });
  }

  changeColorOptions(evt) {
    // Optimization: Safely access nested properties and use the correct path provided
    const color = evt.detail?.data?.options[0]?.value || null;
    if (color) {
      this.setAttribute('data-color', color);
    }
  }

  updateRowsByColor(color) {
    if (!color) return;
    
    this.rows.forEach(row => {
      const select = row.querySelector('.matching-row__select');
      if (!select) return;

      const matchedOption = this.findOptionByColor(select, color);

      if (matchedOption) {
        // Update native select value
        select.value = matchedOption.value;
        
        // Update UI (including Custom Select, image, price, etc.)
        this.updateRowUI(row, matchedOption);
      }
    });
  }

  // Cleanup when element is removed
  disconnectedCallback() {
    document.removeEventListener('click', this.handleOutsideClick);
  }

  updateRowUI(row, selectedOption) {
    const image = row.querySelector('.matching-row__image img');
    const price = row.querySelector('.matching-row__price');
    const hiddenId = row.querySelector('input[name="id"]');
    const addBtn = row.querySelector('[data-add-to-cart]');
    
    // Sync Custom Select UI
    const customOptions = row.querySelectorAll('.custom-option');
    const triggerSpan = row.querySelector('.custom-select__trigger span');
    
    if (customOptions.length > 0) {
      customOptions.forEach(opt => {
        const isMatch = opt.dataset.value === selectedOption.value;
        opt.classList.toggle('selected', isMatch);
        if (isMatch && triggerSpan) {
          triggerSpan.textContent = opt.textContent;
        }
      });
    }

    const newImage = selectedOption.dataset.image;
    const newPrice = parseFloat(selectedOption.dataset.price);
    const newVariantId = selectedOption.value;
    const inventory = parseInt(selectedOption.dataset.inventory, 10);
    const continueSelling = selectedOption.dataset.continueSelling === 'true' || selectedOption.dataset.inventoryPolicy === 'continue';

    // Update details link to current selected variant's product
    const detailsLink = row.querySelector('.matching-row__details');
    const productUrl = selectedOption.dataset.productUrl;
    const productHandle = selectedOption.dataset.productHandle;
    if (detailsLink) {
      if (productUrl) detailsLink.href = productUrl;
      if (productHandle) detailsLink.dataset.handle = productHandle;
    }

    // Update image with proper srcset handling
    if (newImage && newImage.trim() !== '') {
      image.src = newImage;
      const img100 = selectedOption.dataset.image100;
      const img200 = selectedOption.dataset.image200;
      
      if (img100 && img200 && img100.trim() !== '' && img200.trim() !== '') {
        image.srcset = `${img100} 100w, ${img200} 200w`;
        image.sizes = '100px';
      } else {
        // Remove srcset if no responsive images available
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
      }
    } else {
      // No image available, try to find and use placeholder
      const placeholderImg = document.querySelector('img[src*="blank.svg"]');
      if (placeholderImg) {
        image.src = placeholderImg.src;
      }
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
    }
    if (!isNaN(newPrice)) {
      const formattedPrice = (window.theme && theme.settings && theme.settings.currency_code_enable)
        ? window.theme.formatMoney(newPrice, theme.moneyWithCurrencyFormat)
        : window.theme.formatMoney(newPrice, theme.moneyFormat);
      price.innerHTML = formattedPrice;
    }
    if (newVariantId) {
      hiddenId.value = newVariantId;
    }
    const available = (inventory > 0) || continueSelling;
    addBtn.disabled = !available;
  }

  initShowMore() {
    const showMoreContainer = this.querySelector('#show-more-container');
    const showLessContainer = this.querySelector('#show-less-container');
    const showMoreBtn = this.querySelector('#show-more-btn');
    const showLessBtn = this.querySelector('#show-less-btn');
    const rows = this.querySelectorAll('.matching-row');

    if (showMoreBtn) {
      showMoreBtn.addEventListener('click', () => {
        rows.forEach(row => row.classList.remove('hidden'));
        showMoreContainer.classList.add('hidden');
        if (showLessContainer) {
          showLessContainer.classList.remove('hidden');
        }
      });
    }

    if (showLessBtn) {
      showLessBtn.addEventListener('click', () => {
        rows.forEach((row, index) => {
          if (index >= 4) {
            row.classList.add('hidden');
          }
        });
        showLessContainer.classList.add('hidden');
        if (showMoreContainer) {
          showMoreContainer.classList.remove('hidden');
        }
      });
    }
  }

  setupRow(row) {
    const select = row.querySelector('.matching-row__select');
    select.addEventListener('change', (event) => {
      const selectedOption = event.target.options[event.target.selectedIndex];
      this.updateRowUI(row, selectedOption);
    });

    const customSelect = row.querySelector('.custom-select');
    const trigger = row.querySelector('.custom-select__trigger');
    const customOptions = row.querySelectorAll('.custom-option');

    trigger.addEventListener('click', () => {
      customSelect.classList.toggle('open');
    });

    customOptions.forEach(option => {
      option.addEventListener('click', (e) => {
        e.preventDefault();
        const value = option.dataset.value;
        const text = option.textContent;
        
        // Remove selected class from all options
        customOptions.forEach(opt => opt.classList.remove('selected'));
        // Add selected class to clicked option
        option.classList.add('selected');
        
        // Update hidden select
        select.value = value;
        select.dispatchEvent(new Event('change'));
        
        // Update custom trigger text
        trigger.querySelector('span').textContent = text;
        
        // Close dropdown
        customSelect.classList.remove('open');
      });
    });

    const addBtn = row.querySelector('[data-add-to-cart]');
    if (addBtn) {
      addBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        const variantId = row.querySelector('input[name="id"]').value;
        if (!variantId) return;

        addBtn.classList.add('loading');
        
        try {
          const formData = new FormData();
          formData.append('id', variantId);
          formData.append('quantity', 1);

          // Get root url safely
          const root = window.theme?.routes?.root || window.Shopify?.routes?.root || '/';
          const cartAddUrl = `${root.endsWith('/') ? root : root + '/'}cart/add.js`;

          const response = await fetch(cartAddUrl, {
            method: 'POST',
            body: formData
          });

          if (response.ok) {
            // Dispatch event to update cart drawer/count
            document.dispatchEvent(new CustomEvent('cart:item-added'));
            document.dispatchEvent(new CustomEvent('cart:updated'));
            // Trigger drawer update specifically for this theme
            document.dispatchEvent(new CustomEvent('cart:drawer:update'));
            
            // Optional: visual feedback
            const originalText = addBtn.textContent;
            addBtn.textContent = '✔';
            setTimeout(() => {
              addBtn.textContent = originalText;
            }, 2000);
          } else {
            console.error('Failed to add item to cart');
          }
        } catch (error) {
          console.error('Error adding to cart:', error);
        } finally {
          addBtn.classList.remove('loading');
        }
      });
    }
  }
}

if (!customElements.get('product-matching-rows')) {
  customElements.define('product-matching-rows', ProductMatchingRows);
}